import React from 'react';
import {
  MappingContext,
  newMappingContext,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/MappingContext';
import {
  IPropertyChangeHandler,
  BeforeSaveCallback,
  IQuickChangeAction,
  IShowDialogAction,
} from 'components/property-editors/PropertyGrid/PropertyGrid';
import { IPropertyChild } from 'components/property-editors/utils/ProviderNodeUtils';
import { LegacyEditorOptions, RenderLegacyEditor } from 'components/Editors/legacy-editors/LegacyEditor';
import { ITabbingContext, TabbingContext } from 'contexts/TabbingContext';
import { ScrollProvider } from 'contexts/ScrollContext';
import { AwaitableContext, IAwaitableContext } from 'contexts/AwaitableContext';

import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties';
import { getEditingContext } from '../utils/EditingContextUtils';

export type ShowDialogueHandler = (action: IShowDialogAction) => void | Promise<void>;
/**
 * Quick action handler function
 */
export type QuickChangeActionHandler = (child: IPropertyChild, action: IQuickChangeAction, onDone?: () => void) => void;
export type AddSaveCallbackHandler = (path: string[], callback: BeforeSaveCallback) => void;

export interface IPropertyEditorContext {
  editingContextId: string;
  /** this is something like a `formDesignerSessionInfoId`, not Decisions SessionId */
  sessionId: string;
  mappingContext: MappingContext;
  onChange: IPropertyChangeHandler;
  onQuickChange: QuickChangeActionHandler;
  onShowDialogue: ShowDialogueHandler;
  refreshContext: () => void;
  refresh: () => void;
  addBeforeSaveCallback: AddSaveCallbackHandler;
  searchString: string;
  renderLegacyEditor?: RenderLegacyEditor;
}

/** default context, exported really just for unit tests. */
export const defaultPropertyEditorContext: IPropertyEditorContext = {
  editingContextId: '',
  mappingContext: newMappingContext({}),
  sessionId: '',
  onChange: async (value: any, path: string[]) => {
    console.warn('Property changes not connected', value, path);
  },
  onQuickChange: (child: IPropertyChild, action: IQuickChangeAction) =>
    console.warn('Property Quick Actions not handled', action),
  onShowDialogue: (action: IShowDialogAction) => {
    console.warn('Editor dialogues not handled', action.node.Node.Name, action.path);
  },
  /** Refresh context based changes editors have made going straight to the server. */
  refreshContext: () => {},
  refresh: () => {},
  addBeforeSaveCallback: (path: string[], callback: BeforeSaveCallback) => {},
  searchString: '',
};

export const PropertyEditorContext = React.createContext<IPropertyEditorContext>({ ...defaultPropertyEditorContext });

interface IPropertyEditorContextProviderValue {
  awaitable: IAwaitableContext;
  children: React.ReactNode;
  tabs: ITabbingContext;
  value: IPropertyEditorContext;
  editorScrollContainer: HTMLElement | null;
}

export function PropertyEditorContextProvider({
  awaitable,
  children,
  tabs,
  value,
  editorScrollContainer,
}: IPropertyEditorContextProviderValue) {
  return (
    <PropertyEditorContext.Provider value={value}>
      <TabbingContext.Provider value={tabs}>
        <ScrollProvider scrollParent={editorScrollContainer}>
          <AwaitableContext.Provider value={awaitable}>{children}</AwaitableContext.Provider>
        </ScrollProvider>
      </TabbingContext.Provider>
    </PropertyEditorContext.Provider>
  );
}

/**
 * interface for configuring a context via this helper
 *
 * TODO too many of these are probably optional
 */
export interface IHasPropertyEditorProps {
  sessionId?: string;
  isCustomContext?: boolean
  mappingContext?: MappingContext;
  editingContextId?: string;
  onShowDialogue?: ShowDialogueHandler;
  snapshot?: ContextProviderNode; // this could be the only required one... or maybe just an editing context ID
  onQuickChange?: QuickChangeActionHandler;
  addBeforeSaveCallback?: AddSaveCallbackHandler;
  renderLegacyEditor?: RenderLegacyEditor;
  /** true to force loading state */
  loading?: {
    isLoading: boolean;
    height: number;
  };
}

export function propertyEditorContextHelper(
  props: IHasPropertyEditorProps,
  defaultContext: IPropertyEditorContext,
  searchString: string
) {
  const context: IPropertyEditorContext = {
    ...defaultContext,
    editingContextId: getEditingContext(props),
    mappingContext: props.mappingContext || defaultContext.mappingContext,
    searchString,
  };

  // overwrite optional handlers with handlers from props, if defined:
  context.sessionId = props.sessionId || context.sessionId;
  // make this dialog workaround the normal fallback for now...
  context.onShowDialogue =
    props.onShowDialogue ||
    ((window as any).$DP && (props.editingContextId || props.snapshot)
      ? (action: IShowDialogAction) => {
          const mappingContext = $DP.Common.MappingContext?.create?.(props.mappingContext) // pass mapping context if we have it
          if (props.isCustomContext) {
            mappingContext?.setForCustom(props.isCustomContext)
          }
          return $DP.PropertyGrid.Util.launchDialogEditor(
            action,
            props.editingContextId ?? props.snapshot?.EditingContextId,
            mappingContext
            // onDone?: (updatedValue?: any) => void,
          );
        }
      : context.onShowDialogue);
  context.onQuickChange = props.onQuickChange || context.onQuickChange;
  context.addBeforeSaveCallback = props.addBeforeSaveCallback || context.addBeforeSaveCallback;
  context.renderLegacyEditor =
    props.renderLegacyEditor ||
    ((window as any).$DP && (props.editingContextId || props.snapshot)
      ? (options: LegacyEditorOptions) => {
          $DP.PropertyGrid.Util.renderLegacyEditor(options);
        }
      : context.renderLegacyEditor);
  return context;
}
